<template>
  <v-app
    id="gw_main"
    :class="[`locale_${$i18n.locale}`, `route_${$route.name}`]"
    :style="`${['Play', 'AnalyzeHandsV3', 'GtoReports', 'AnalyzeFilesV4', 'AnalyzeHandsV4', 'CryptoGateway'].includes($route.name) ? '--clr-primary:var(--app-clr-primary-v2); --clr-white-in-primary:var(--clr-black-0);' : ''}`"
  >
    <AppLoadingStatus
      v-if="showAppLoading"
      class="anim-fade-in"
      :loading="showLoader"
      :errors="initStatus.errors"
      :update="initStatus.update"
      :protectionDelay="initStatus.protectionDelay"
    />
    <CanvasPaint v-if="!showAppLoading && initStatus.initialized && !mobile" />
    <AppMessages v-if="!showAppLoading" />

    <!-- <GlobalTooltip v-if="!mobile" /> -->
    <!-- <GwSwipeModal></GwSwipeModal> -->
    <Messages />
    <AppDialogs v-if="!showAppLoading && initStatus.initialized" />
    <portal-target
      name="main_before"
      multiple
    />
    <!-- hack for recursive Portals as inside mennus dont work well in safari Vue3 compared to Vue2 - TODO handle better -->
    <portal-target
      name="main_before1"
      multiple
    />

    <component
      :is="$route.meta.layout || 'div'"
      v-if="!showAppLoading || $route.meta.routeAlwaysVisible"
    >
      <router-view />

      <Suspense>
        <template #default>
          <ReactAppMount />
        </template>
      </Suspense>

      <Drawer />
      <portal-target
        name="main_inside"
        multiple
      />
    </component>

    <InstallBox
      v-if="showPwaInstallBox && mobile"
      @install="installPwa"
      @close="
        () => {
          showPwaInstallBox = false
          $store.commit('globals_pwa_installed', false)
        }
      "
    >
    </InstallBox>
  </v-app>
</template>

<script>
import '@gto/ui/styles/global.css'
import Messages from '@/components/Messages/Messages'
import InstallBox from '@/components/SW/InstallBox'
import CanvasPaint from '@/components/Study/CanvasPaint'
import AppMessages from '@/components/Messages/AppMessages'
import { throttle } from 'lodash'
import { getCookie, deleteCookie, setCrossDomainCookie } from '@gto/helpers/cookies'
import { initFreshChat, hideFreshChatBubble } from '@gto/widgets/fresh-chat'

import { trackAffiliate } from '@/helpers/affiliate'
import { langList } from '@/locales/i18n'
import { allSwitchers } from '@/store/switchers/switchers'
import AppLoadingStatus from '@/components/app/AppLoadingStatus'
import AppDialogs from '@/components/Dialogs/AppDialogs'
import Drawer from '@/components/Drawer/Drawer'
import Logo from '@/assets/logo.svg'
import GlobalTooltip from '@/components/lib/GlobalTooltip'
import tour_mix from '@/mixins/tour_mix'
import prompt_mix from '@/mixins/prompt_mix'
import tier_mix from '@/mixins/tier_mix'
import { beforeInstallPromptListener } from '@gto/helpers/install-app'
import ReactAppMount from '@/components/React/ReactAppMount.vue'
import * as R from 'remeda'

// import Messages from '@/components/Messages/Messages'
// import InstallBox from '@/components/SW/InstallBox'
// import CanvasPaint from '@/components/Study/CanvasPaint'
// import AppMessages from '@/components/Messages/AppMessages'

window.GW_APP_COMMIT_SHA = R.isString(import.meta.env.VITE_COMMIT_SHORT_SHA)
  ? import.meta.env.VITE_COMMIT_SHORT_SHA
  : undefined

// TODO removed maxContentWidth --- do so in Vue3 as this module is really refactored there
const getInitStatus = (args = {}) => ({
  loading: false,
  update: false,
  errors: [],
  protectionDelay: 0,
  initialized: false,
  ...args,
})

// TODO vue3 tests
// login
// login >> change route >> logout >> login and be redirected
// AppLoadingStatus states (most importantly protection overlay) - now its disabled for dev

export default {
  name: 'App',
  components: {
    Logo,
    Drawer,
    AppDialogs,
    AppLoadingStatus,
    GlobalTooltip,
    Messages,
    InstallBox,
    CanvasPaint,
    AppMessages,
    ReactAppMount,
    // GwSwipeModal,
  },
  mixins: [tour_mix, prompt_mix, tier_mix],
  data() {
    return {
      initStatus: getInitStatus({ initialized: false, loading: true }),
      showPwaInstallBox: false,
      installPwaPrompt: null,
      cntS: 15,
    }
  },
  created() {
    this.setDialogExceptions()
    this.addEventListeners('pwa')
    this.addEventListeners('global')
    this.preventIOSzoom()
    this.handleResize()

    if (window.location.href.includes('/login')) {
      this.initStatus = getInitStatus({ initialized: false })
      return
    }

    this.initializeApp()
  },
  methods: {
    initializeApp() {
      this.initStatus = getInitStatus({ initialized: false, loading: true })

      const plan = this.$store.dispatch(
        `payments_subscription_plan${this.$store.getters.user_has_flag('ADYEN') ? '_v2' : ''}`,
      )
      const settings = this.$store.dispatch('account_settings')
      const cussolHistory = this.$store.dispatch('api_poker_custom_solution_history_initial')

      let cussolReportsHistory = null
      if (this.$store.getters.user_has_flag('CR_REPORTS')) {
        cussolReportsHistory = this.$store.dispatch('api_custom_reports_history_initial')
      }

      // temporary cos of the survey - we need to know this in advance
      // remove after survey
      let cussolSavedSurvey = null
      if (
        this.$store.getters.user_has_flag('GMF_SURVEY') &&
        localStorage.getItem('surveyTaken3') !== 'true' &&
        this.$store.getters.user_info.subs.some(i => i.plan.tier.toLowerCase() === 'elite')
      ) {
        // this is needed only for elite users
        cussolSavedSurvey = this.$store.dispatch(
          'api_poker_custom_solution_table_initial_for_the_survey',
        )
      }

      Promise.all(
        [
          plan, //
          settings,
          cussolHistory,
          cussolReportsHistory,
          cussolSavedSurvey,
        ].filter(i => i),
      ).then(async values => {
        const status = values.reduce(
          (acc, call) => {
            if (call.update)
              return {
                ...acc,
                update: [...(acc.update || []), call.callId],
              }
            if (call.response?.status !== 200) {
              return { ...acc, error: [...(acc.error || []), call.callId] }
            }
            return acc
          },
          { error: null, update: null },
        )
        if (status.update) {
          this.initStatus = getInitStatus({ update: true })
        } else if (this.$route.name === 'Login') {
          // was forcefully logged out - status.error is true but we dont show errors
          this.initStatus = getInitStatus({ initialized: false })
        } else if (status.error) {
          this.initStatus = getInitStatus({ errors: status.error })
        } else {
          await this.$store.dispatch('gameformats_init_pageViewStores')

          this.initStores()
          this.initTour()

          const settingsCall = values.find(i => i.callId === 'account/settings/')
          this.setAppMessages({ messages: settingsCall?.response?.data?.messages || [] })

          this.sendGCLID()
          setCrossDomainCookie('GTO_LOGGED_IN', 'yes')
          setCrossDomainCookie('GTO_USERID', this.$store.getters.user_info.public_id)
          trackAffiliate()

          if (this.$store.getters.user_has_flag('CHAT_WIDGET')) {
            initFreshChat()
          }

          this.addEventListeners('afterInit')

          this.initStatus = getInitStatus({
            protectionDelay: this.initStatus.protectionDelay, //
            initialized: true,
          })
        }
      })
    },
    initStores() {
      this.$store.dispatch('api_drills_get')
      this.$store.dispatch('api_saved_filters_get')

      this.$store.dispatch('tags_get_tags', { tagType: 'drills' })
      this.$store.dispatch('tags_get_tags', { tagType: 'saved_solutions' })

      this.$store.dispatch('resetColorSettings')
      this.$store.dispatch('switchers_initialize') // also resets colors in actions-grouping
      this.$store.dispatch('switchers_set_defaults')
    },
    setAppMessages({ messages }) {
      if (['ru', 'ru-MI'].some(i => i === this.$i18n.locale || i === navigator.language)) {
        messages.push({
          message:
            'А вы знаете, что у нас есть официальный <span class="weight_700"> youtube канал </span> на русском?',
          type: 'error',
          linkUrl: 'https://www.youtube.com/channel/UCFJMUGsjKsEQB8T0zqPbJ2Q?sub_confirmation=1',
          linkText: 'Нажмите, чтобы подписаться',
          linkIcon: 'mdi-youtube',
          id: 'ru_ytchannelsubscribe',
        })
      }
      if (messages.some(i => i.type === 'ANALYZER_OFFLINE')) {
        this.$store.commit('globals_set', { key: 'maintenanceFilesUpload', value: true })
      }
      this.$store.commit('globals_set', { key: 'appMessages', value: messages })
    },
    setDialogExceptions() {
      if (!this.$route.query.dialogs) return
      // Keep it only if we have a pending payment and were redirected back to our page.
      const keepUpdatePaymentPlanDialog = this.$route.query.pending_payment === 'true'
      const exeptions = [
        'settings-dialog',
        //  'triton-confirm-dialog'
        keepUpdatePaymentPlanDialog ? 'update-payment-plan-dialog' : '',
        this.$route.name === 'StudySolutions' ? 'solfigr-create-custom-solution-dialog' : '',
      ].filter(i => i)
      let res = this.$route.query.dialogs.split(',')
      res = res.filter(d => exeptions.some(e => d.includes(e)))
      res = [...new Set(res)]
      res = res.join(',')
      this.$router.replace({ query: { ...this.$route.query, dialogs: res } }).catch(() => {})
    },
    sendGCLID() {
      // google ads gclid
      const gclid = getCookie('GTO_GCLID')
      if (gclid) {
        this.$store.dispatch('account_settings_patch', { data: { gclid } })
        console.log('GTO_GCLID sent', gclid)
        deleteCookie('GTO_GCLID')
      }
    },
    addEventListeners(id) {
      if (id === 'global') {
        window.addEventListener('resize', this.handleResize)
        return
      }
      if (id === 'pwa') {
        // PWA install prompts
        if (this.$store.getters.user_has_flag('REACT_MOBILE_MENU')) {
          window.addEventListener('beforeinstallprompt', beforeInstallPromptListener, {
            once: true,
          })
        } else {
          window.addEventListener('beforeinstallprompt', this.handlePwaInstallPrompt, {
            once: true,
          })
        }
        window.addEventListener('appinstalled', this.handlePwaInstalled)
        return
      }
      if (id === 'afterInit') {
        window.addEventListener('beforeunload', this.handleBeforeUnload)
        window.addEventListener('keydown', this.handleKeydown)
        window.addEventListener('keyup', this.handleKeyup)
        window.addEventListener('blur', this.handleBlur)
      }
    },
    handlePwaInstallPrompt(e) {
      e.preventDefault()
      this.showPwaInstallBox = true
      this.installPwaPrompt = e
      this.$store.commit('globals_pwa_installed', false)
    },
    async installPwa() {
      this.showPwaInstallBox = false
      if (this.installPwaPrompt) {
        this.installPwaPrompt.prompt()
        const { outcome } = await this.installPwaPrompt.userChoice
        if (outcome === 'accepted') {
          this.installPwaPrompt = null
          this.$store.commit('globals_pwa_installed', true)
        }
      }
    },
    handlePwaInstalled() {
      this.showPwaInstallBox = false
      this.installPwaPrompt = null
      this.$store.commit('globals_pwa_installed', true)
    },
    preventIOSzoom() {
      // prevent ios zoom in inputs
      // https://weblog.west-wind.com/posts/2023/Apr/17/Preventing-iOS-Safari-Textbox-Zooming
      if (navigator.userAgent.indexOf('iPhone') > -1) {
        document
          .querySelector('[name=viewport]')
          .setAttribute(
            'content',
            'width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=1',
          )
      }
    },
    handleRouteChange() {
      if (this.$route.name === 'Login') {
        this.$store.dispatch('globals_drawer', {
          open: false,
          id: '',
        })

        this.initStatus = getInitStatus({ initialized: false })
        hideFreshChatBubble()
        return
      }

      if (!this.initStatus.initialized) {
        this.initializeApp()
        return
      }

      if (!this.mobile) {
        this.cancelTour()
        setTimeout(() => {
          this.startTour()
        }, 200)
      }

      this.$store.dispatch('globals_drawer', {
        open: false,
        id: '',
      })
    },
    handleResize: throttle(function () {
      const height = window.innerHeight
      const width = window.innerWidth
      this.$store.commit('globals_mobile', width <= 1023)

      this.setMobileBrowserVhUnits(height)

      this.$store.commit('globals_window_size', { width, height })

      let hsExpanded = localStorage.getItem('hsExpanded') || null
      if (hsExpanded) {
        hsExpanded = hsExpanded === 'expanded' // not-expanded
      }

      if (height < 781 || width < 1366) {
        this.$store.dispatch('globals_hs_open', false)
        this.$store.dispatch('globals_fullscreen', width <= 1023 ? true : hsExpanded ?? true)
      } else {
        this.$store.dispatch('globals_hs_open', false)
        this.$store.dispatch('globals_fullscreen', width <= 1023 ? false : hsExpanded ?? false)
      }
    }, 120),
    setMobileBrowserVhUnits() {
      // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
      let vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)

      setTimeout(() => {
        vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
      }, 333)
      setTimeout(() => {
        vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
      }, 1666)
    },

    handleBeforeUnload(e) {
      const uploadInProgress = this.$store.state.api.api_uploads.some(
        i => i.status === 'progress' || i.status === 'waiting',
      )
      if (uploadInProgress) {
        e.preventDefault()
        e.returnValue = 'Upload in progress'
      }
      localStorage.setItem('redirect', this.$router.currentRoute.value.fullPath)
    },
    handleBlur() {
      this.$store.dispatch('hotkeys_reset')
    },
    handleKeyup(e) {
      this.$store.dispatch('hotkeys_keyup', e)
    },
    handleKeydown: throttle(function (e) {
      if (
        import.meta.env.VITE_ENV === 'development' ||
        import.meta.env.VITE_BRANCH === 'dev' ||
        import.meta.env.VITE_BRANCH === 'staging'
      ) {
        if (e.code === 'Backquote' || e.code === 'IntlBackslash') {
          const idx = langList.findIndex(i => i === this.$i18n.locale)
          this.$i18n.locale = langList[idx + 1] ? langList[idx + 1] : langList[0]
          localStorage.setItem('lang', this.$i18n.locale)
          window.dispatchEvent(
            new CustomEvent('languageChange', { detail: { lang: this.$i18n.locale } }),
          )
        }
      }
      // -----------
      if (this.tour) return
      const tagName = document.activeElement?.tagName.toLowerCase()
      if (tagName === 'textarea') return
      if (tagName === 'input') return

      this.$store.dispatch('hotkeys_keydown', e)
    }, 40),
    switchModeByKey() {
      const { width } = this.$store.getters.globals_window_size
      if (width >= 1024) {
        this.$store.dispatch('globals_focus_mode')
      }
      if (this.$store.getters.focus_mode) {
        this.pr_dispatchMessage({ id: 'focus' })
      }
    },
  },
  computed: {
    isDev() {
      return import.meta.env.VITE_ENV !== 'production'
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
    globals_action() {
      return this.$store.getters.globals_action
    },
    hotkeysPressed() {
      return this.$store.getters.hotkeys_pressed
    },
    loginInProgress() {
      return this.$store.getters.api_get('api_auth').loading
    },
    showLoader() {
      return this.initStatus.loading || this.loginInProgress
    },
    showAppLoading() {
      return (
        this.loginInProgress ||
        this.initStatus.loading ||
        this.initStatus.errors.length ||
        this.initStatus.update ||
        this.initStatus.protectionDelay
      )
    },
  },
  watch: {
    '$route.name': {
      immediate: false,
      handler: 'handleRouteChange',
    },
    showLoader(show) {
      window.dispatchEvent(new Event(show ? 'loading-start' : 'loading-end'))
    },
    hotkeysPressed(keys) {
      if (!keys) return
      const { hotkeys } = this.$store.getters
      const htkPressed = this.$store.getters.hotkeys_pressed
      const htkPressedBottom = this.$store.getters.hotkeys_pressed_bottom

      const hotkeyActions = {
        switch_pot_bb: {
          keys: hotkeys.switch_pot_bb.keyCombination,
          hasPreventDefault: true,
          pressedKeys: htkPressed,
          fn: () => {
            const settings = this.$store.getters.mergedUserSettings

            const showBetsIn =
              settings.showBetsIn === 'bb'
                ? 'pot'
                : settings.showBetsIn === 'pot'
                  ? 'bb_and_pot'
                  : settings.showBetsIn === 'bb_and_pot'
                    ? 'pot_and_bb'
                    : 'bb'

            this.$store.dispatch('userSettings', { showBetsIn })
            this.pr_dispatchMessage({ id: 'show_bets_in' })
          },
        },
        switch_ev_pot: {
          keys: hotkeys.switch_ev_pot.keyCombination,
          hasPreventDefault: true,
          pressedKeys: htkPressed,
          fn: () => {
            const settings = this.$store.getters.mergedUserSettings

            const isStudyIcmCustomSolution = () => {
              if (this.$route.name !== 'StudySolutions') return false
              const solElite = this.$store.getters.sol_elite
              return solElite?.is_using && solElite.tournamentType === 'icm'
            }
            const isTrainerIcmCustomSolution = () => {
              if (this.$route.name !== 'PracticeTrainer') return false
              const solElite = this.$store.getters['tra/alpha/sol_elite']
              return solElite?.is_using && solElite.tournamentType === 'icm'
            }

            const useICMConverter = isStudyIcmCustomSolution() || isTrainerIcmCustomSolution()
            const converterInUse = useICMConverter ? 'sw_ev_icm_converter' : 'sw_ev_converter'
            const converterOptions = allSwitchers()[converterInUse]

            const settingsKeyInUse = useICMConverter ? 'showEvIcmIn' : 'showEvIn'
            const currentOptionIndex = converterOptions.findIndex(
              i => i.payload === settings[settingsKeyInUse],
            )
            const nextOption = converterOptions[currentOptionIndex + 1] || converterOptions[0]

            const payload = {
              [settingsKeyInUse]: nextOption.payload,
            }

            this.$store.dispatch('userSettings', payload)
            this.pr_dispatchMessage({ id: 'show_ev_in' })
          },
        },
        switch_actions_grouping: {
          keys: hotkeys.switch_actions_grouping.keyCombination,
          pressedKeys: htkPressed,
          fn: () => {
            this.$store.dispatch('switchers_set_next', { key: 'sw_actions_grouping' })
            this.pr_dispatchMessage({ id: 'grouping' })
          },
        },
        focus_mode_on: {
          keys: hotkeys.focus_mode.keyCombination,
          pressedKeys: htkPressedBottom || [], // dont trigger in dialog
          fn: () => {
            this.switchModeByKey()
          },
        },
      }
      this.$store.dispatch('hotkeys_perform_action', { keys, hotkeyActions })
    },
    globals_action(action) {
      if (action === 'install_pwa') {
        this.installPwa()
        this.$store.dispatch('globals_action_reset')
      }
    },
  },
}
</script>
